import * as React from 'react';
import { fetchBidderDataIfNeeded, getAuthToken, isBidderLoaded, isUserLoggedIn } from '@/redux/modules/user';
import { useDispatch, useSelector } from 'react-redux';
import AppHeaderMeta from './AppHeaderMeta';
import Auth from './Auth';
import BackToTop from '@/components/BacktoTop/BackToTop';
import BannerOutdatedBrowser from '@/components/BannerOutdatedBrowser/BannerOutdatedBrowser';
import Cookies from 'universal-cookie';
import CurrencySelectionPopover from '@/components/CurrencySelection/CurrencySelectionPopover';
import DismissableGetMobileBanner from '@/components/DismissableGetMobileBanner/DismissableGetMobileBanner';
import DismissableGetWebNotificationsBanner from '@/components/DismissableGetWebNotificationsBanner/DismissableGetWebNotificationsBanner';
import FeedbackLink from '@/components/FeedbackLink/FeedbackLink';
import { fetchCurrencyConversionsIfNeeded } from '@/redux/modules/currency';
import { fetchLAPaymentCardIfNeeded } from '@/redux/modules/creditCard';
import { fetchUserVariantsIfNeeded } from '@/redux/modules/userVariantTest';
import Footer from '@/components/Footer/Footer';
import ForgotPasswordAction from '@/components/Modals/ForgotPassword/ForgotPasswordAction';
import GeoLocationSearchExperimentContainer from '@/components/ExperimentSetup/GeoLocationSearchExperimentContainer';
import { getIsWhiteLabel } from '@/redux/modules/whiteLabel';
import { getPageSizeFromCookie } from '@/utils/pagination';
import GoogleTagManager from '@liveauctioneers/caterwaul-components/lib/GoogleTagManager/GoogleTagManager';
import Header from '@/components/Header/Header';
import { hydrateClientState } from '@/redux/modules/client';
import LogRocket from '@/components/LogRocket/LogRocket';
import Modals from '@/components/Modals/Modals';
import NewSafariSentinel from '@/components/NewSafariSentinel/NewSafariSentinel';
import NotificationCountTitle from '@/components/NotificationCountTitle/NotificationCountTitle';
import { parseQueryParams } from '@/utils/queryParams';
import PubnubBidderNotificationContainer from '@/components/PubnubContainer/PubnubBidderNotificationContainer';
import PubnubGlobalNotificationContainer from '@/components/PubnubContainer/PubnubGlobalNotificationContainer';
import SailthruInit from '@/components/Sailthru/SailthruInit';
import SegmentTracker from '@/components/Segment/SegmentTracker';
import Signifyd from '@/components/Signifyd/Signifyd';
import SignupAction from '@/components/Modals/SignUp/SignupAction';
import styled from 'styled-components';
import SuspendedBidderBanner from '@/components/SuspendedBidderBanner/SuspendedBidderBanner';
import TermsOfServiceBanner from '@/components/TermsOfServiceBanner/TermsOfServiceBanner';
import ToastMessage from '@liveauctioneers/caterwaul-components/lib/ToastMessage/ToastMessage';
import TrustBoxScript from '@/components/TrustBox/TrustBoxScript';
import { useLocation } from 'react-router-dom';
import WindowSizeContainer from '@/components/WindowSizeContainer/WindowSizeContainer';

const cookie = new Cookies();

type Props = {
    children: React.ReactNode;
};

const App = ({ children }: Props) => {
    const { pathname, search } = useLocation();
    const dispatch = useDispatch();
    const [isClientHydrated, setClientHydrated] = React.useState(false);
    const authToken = useSelector(getAuthToken);
    const bidderIsLoaded = useSelector(isBidderLoaded);
    const { hideForTesting }: any = parseQueryParams(search);
    const userLoggedIn = useSelector(isUserLoggedIn);
    const biddingConsole = pathname.includes('/console/');
    const isWhiteLabel = useSelector(getIsWhiteLabel);

    const hydrateClient = React.useCallback(async () => {
        const token = cookie.get('bidder-auth') || '';
        const dateLastShown = cookie.get('join-modal-last-seen') || '';
        const hasLoggedInBefore = Boolean(cookie.get('bidder-has-logged-in')) || Boolean(token);
        const pageSizeCookie = cookie.get('pagination-page-size') || 24;
        const pageSize = getPageSizeFromCookie(pageSizeCookie);

        const disableModalForTesting = biddingConsole || hideForTesting === 'true';

        dispatch(
            hydrateClientState({
                dateLastShown,
                disableModalForTesting,
                hasLoggedInBefore,
                pageSize,
                token,
            })
        );
    }, [dispatch, biddingConsole, hideForTesting]);

    React.useEffect(() => {
        if (!bidderIsLoaded && authToken) {
            dispatch(fetchBidderDataIfNeeded(authToken, false));
        }
    }, [dispatch, authToken, bidderIsLoaded]);

    React.useEffect(() => {
        if (!isClientHydrated) {
            hydrateClient().then(() => {
                setClientHydrated(true);
                return Promise.all([
                    dispatch(fetchBidderDataIfNeeded(authToken)),
                    dispatch(fetchUserVariantsIfNeeded()),
                    dispatch(fetchCurrencyConversionsIfNeeded()),
                    dispatch(fetchLAPaymentCardIfNeeded()),
                ]);
            });
        }

        return () => {
            window.onstorage = null;
        };
    }, [authToken, dispatch, hydrateClient, isClientHydrated]);

    const showHeaderAndFooter = !biddingConsole;

    return (
        <React.StrictMode>
            <Signifyd />
            <TrustBoxScript />
            <GeoLocationSearchExperimentContainer />
            <LogRocket />
            <FeedbackLink />
            {userLoggedIn && <PubnubBidderNotificationContainer />}
            <TermsOfServiceBanner />
            <DismissableGetMobileBanner />
            {userLoggedIn && <DismissableGetWebNotificationsBanner />}
            <SuspendedBidderBanner />
            {/* <CustomToastContainer /> */}
            <ToastMessage />
            <PubnubGlobalNotificationContainer />
            <NotificationCountTitle /> {/** TODO: update this */}
            <NewSafariSentinel />
            <Auth />
            {isClientHydrated && <SignupAction />}
            {isClientHydrated && <ForgotPasswordAction />}
            <StyledApp>
                {isWhiteLabel && <GoogleTagManager gtmId="GTM-MGNHR3N" />}
                {isWhiteLabel && <GoogleTagManager gtmId="GTM-T68B3J7" />}
                {isWhiteLabel && <GoogleTagManager gtmId="GTM-5CJ3DX7" />}
                <WindowSizeContainer /> {/** TODO: update this */}
                <AppHeaderMeta />
                <SailthruInit />
                <BannerOutdatedBrowser />
                {showHeaderAndFooter && <Header />}
                <StyledAppContent id="main">{children}</StyledAppContent>
                {showHeaderAndFooter && <Footer />}
                <Modals />
                <CurrencySelectionPopover />
                <SegmentTracker />
                <BackToTop />
            </StyledApp>
        </React.StrictMode>
    );
};

export default React.memo(App);

const StyledApp = styled.div`
    height: 100%;
    width: 100%;
    position: relative;

    @media print {
        overflow: initial;
    }
`;

const StyledAppContent = styled.div`
    height: 100%;

    @media (min-width: 1240px) {
        margin: 0 auto;
    }

    @media print {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        bottom: 0;
        margin: auto;
        margin-top: 0 !important;
    }
`;
